import {DataGrid} from "@mui/x-data-grid/DataGrid";
import React, {useCallback, useEffect, useState} from "react";
import {Box, Button, Pagination, Snackbar, Stack, Typography} from "@mui/material";
import {useNavigate} from "react-router-dom";
import "./Admin.css";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {SponsorListItem, TierListItem} from "../../data/sponsorInfo";
import {getSponsorsList, getSponsorTiers} from "../../services/SponsorService";
import AddIcon from "@mui/icons-material/Add";
import EditSponsorModal from "../sponsor/EditSponsorModal";
import AddSponsorModal from "../sponsor/AddSponsorModal";
import DeleteSponsorModal from "../sponsor/DeleteSponsorModal";
import {getPartnersList} from "../../services/partnerInformation";
import {PartnerListItem} from "../../data/partnerData";

interface SponsorsProps {
    eventId: string;
    onBackButtonClick: () => void;
}

export default function Sponsors({eventId, onBackButtonClick}: SponsorsProps) {
    const navigate = useNavigate();
    const [sponsors, setSponsors] = useState<SponsorListItem[]>([]);
    const [tiers, setTiers] = useState<TierListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedRow, setSelectedRow] = useState<SponsorListItem | null>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [isAddModalOpen, setAddModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [snackbarDeleteOpen, setSnackbarDeleteOpen] = useState(false);
    const [partners, setPartners] = useState<PartnerListItem[]>([]);
    // Add a refresh trigger state
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            if (!eventId) return;
            console.log(`Fetching data for event: ${eventId}`);

            setLoading(true);
            try {
                // Fetch both sponsors and tiers in parallel
                const [sponsorsResponse, tiersResponse] = await Promise.all([
                    getSponsorsList({ eventId }),
                    getSponsorTiers(eventId)
                ]);

                if (!isMounted) return;

                // Store the tiers
                if (tiersResponse && tiersResponse.length > 0) {
                    setTiers(tiersResponse);
                    console.log(`Successfully loaded ${tiersResponse.length} sponsor tiers`);
                } else {
                    setTiers([]);
                    console.log("No sponsor tiers found for this event");
                }

                // Process sponsors and add tier names
                if (sponsorsResponse && sponsorsResponse.length > 0) {
                    // Map sponsors to include tier names
                    const sponsorsWithTiers = sponsorsResponse.map(sponsor => {
                        const matchingTier = tiersResponse.find(
                            tier => tier.SponsorshipTierId === sponsor.SponsorshipTierId
                        );
                        return {
                            ...sponsor,
                            TierName: matchingTier ? matchingTier.Name : 'Unknown'
                        };
                    });

                    setSponsors(sponsorsWithTiers);
                    console.log(`Successfully loaded ${sponsorsWithTiers.length} sponsors`);
                } else {
                    setSponsors([]);
                    console.log("No sponsors found for this event");
                }
            } catch (error) {
                if (!isMounted) return;
                console.error('Failed to fetch data:', error);
                setSponsors([]);
                setSnackbarMessage('Failed to load data. Please try again later.');
                setSnackbarOpen(true);
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchData();
        getPartners();

        return () => {
            isMounted = false;
        };
    }, [eventId, refreshTrigger]); // Add refreshTrigger to the dependency array

    const getPartners = async () => {
        try {
            const partnersList = await getPartnersList();
            setPartners(partnersList);
        } catch (error) {
            console.error("Error fetching partners:", error);
        }
    };

    const refreshSponsors = useCallback(async () => {
        // Increment the refresh trigger to cause the useEffect to run again
        setRefreshTrigger(prev => prev + 1);
        // Return a resolved promise to satisfy the Promise<void> return type
        return Promise.resolve();
    }, []);

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    const getCurrentItems = useCallback(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return sponsors.slice(startIndex, endIndex);
    }, [sponsors, currentPage]);

    const toggleDrawer = (sponsor: SponsorListItem | null) => {
        setSelectedRow(sponsor);
        setDrawerOpen(!!sponsor);
    };

    const handleSnackbarDeleteOpen = () => setSnackbarDeleteOpen(true);
    const handleSnackbarDeleteClose = (event: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarDeleteOpen(false);
    };
    const handleDelete = () => {
        setDeleteModalOpen(true);
    };

    const columns = [
        {
            field: 'LogoSmall', headerName: '', width: 80,
            renderCell: (params: any) => {
                if (params.value) {
                    return (
                        <img
                            src={params.value}
                            alt={`${params.row.Name} logo`}
                            style={{
                                width: '60px',
                                height: '60px',
                                objectFit: 'contain',
                                margin: 'auto',
                                display: 'block'
                            }}
                        />
                    );
                }
                return <div>No logo</div>;
            }
        },
        {field: 'Name', headerName: 'Name', width: 240},
        {field: 'TierName', headerName: 'Tier', width: 100},
        {field: 'Notes', headerName: 'Notes', width: 400, cellClassName: 'wrapped-cell'},
        {
            field: 'contributionDetails',
            headerName: '',
            width: 200,
            renderCell: (params: any) => (
                <Button
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleDrawer(params.row);
                    }}
                    variant="text"
                    size="small"
                    endIcon={<MoreHorizIcon/>}
                    sx={{
                        '&:hover': {
                            textDecoration: 'underline'
                        }
                    }}
                >
                    Contribution Details...
                </Button>
            ),
        },
    ];

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, p: 2}}>
            <Stack direction="row" spacing={2} justifyContent="flex-start">
                <Button variant="contained" className="editOrAdd" onClick={() => setAddModalOpen(true)}
                        startIcon={<AddIcon/>}>Add Sponsor</Button>
            </Stack>
            <Box sx={{
                display: 'flex',
                transition: 'all 0.3s ease',
                width: drawerOpen ? 'calc(100%)' : '100%',
                position: 'relative'
            }}>
                <Box sx={{
                    width: drawerOpen ? '60%' : '100%',
                    transition: 'width 0.3s ease'
                }}>
                    {loading ? (
                        <Typography>Loading sponsors data...</Typography>
                    ) : sponsors.length > 0 ? (
                        <DataGrid
                            rows={getCurrentItems()}
                            columns={columns}
                            getRowId={(row) => row.SponsorId}
                            className="datagrid"
                            autoHeight
                            disableRowSelectionOnClick
                            hideFooterPagination={true}
                            sx={{
                                '& .wrapped-cell': {
                                    whiteSpace: 'normal !important',
                                    wordWrap: 'break-word !important',
                                    lineHeight: '1.2 !important',
                                    padding: '8px'
                                }
                            }}
                        />
                    ) : (
                        <DataGrid
                            rows={[]}
                            columns={columns}
                            className="datagrid"
                            autoHeight
                            disableRowSelectionOnClick
                            hideFooterPagination={true}
                            localeText={{
                                noRowsLabel: 'No sponsors found for this event.'
                            }}
                        />
                    )}
                </Box>

                <Box
                    sx={{
                        width: drawerOpen ? '40%' : 0,
                        transition: 'width 0.3s ease',
                        borderLeft: drawerOpen ? '1px solid rgba(0, 0, 0, 0.12)' : 'none',
                        overflow: 'hidden'
                    }}
                >
                    {selectedRow && (
                        <Box sx={{p: 3}}>
                            <Typography variant="h6" gutterBottom>
                                View or Make Changes to {selectedRow.Name}
                            </Typography>
                            <Box sx={{mt: 2}}>
                                <Typography color="text.secondary" gutterBottom>
                                    Commitment Date
                                </Typography>
                                <Typography>
                                    {selectedRow.CommitmentDate ? new Date(selectedRow.CommitmentDate).toLocaleDateString() : 'No date available'}
                                </Typography>
                            </Box>
                            <Box sx={{mt: 2}}>
                                <Typography color="text.secondary" gutterBottom>
                                    Donation Amount
                                </Typography>
                                <Typography>
                                    {selectedRow.DonationAmount ?
                                        new Intl.NumberFormat('en-US', {
                                            style: 'currency',
                                            currency: 'USD'
                                        }).format(selectedRow.DonationAmount) :
                                        'No donation amount available'}
                                </Typography>
                            </Box>
                            <Box sx={{mt: 4, display: 'flex', gap: 2, flexDirection: 'column', alignItems: 'center'}}>
                                <Button
                                    variant="contained"
                                    className="editOrAdd"
                                    onClick={() => setEditModalOpen(true)}
                                    startIcon={<EditIcon/>}
                                    sx={{minWidth: '150px'}}
                                >
                                    Edit Sponsor
                                </Button>
                                <Button
                                    variant="contained"
                                    className="delete"
                                    onClick={handleDelete}
                                    startIcon={<DeleteIcon/>}
                                    sx={{minWidth: '150px'}}
                                >
                                    Delete
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>

            {sponsors.length > 0 && (
                <Pagination
                    count={Math.ceil(sponsors.length / itemsPerPage)}
                    page={currentPage}
                    onChange={(e, page) => handlePageChange(page)}
                    color="primary"
                    className="pagination"
                />
            )}
            <DeleteSponsorModal
                isOpen={isDeleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                sponsor={selectedRow}
                eventId={eventId || ''}
                refreshSponsors={refreshSponsors}
                setSnackbarDeleteOpen={handleSnackbarDeleteOpen}
                setDrawerOpen={setDrawerOpen}
                setSelectedRow={setSelectedRow}
            />
            <EditSponsorModal isOpen={editModalOpen} onClose={() => setEditModalOpen(false)} sponsor={selectedRow}
                              setSnackbarMessage={setSnackbarMessage} setSnackbarOpen={setSnackbarOpen}
                              refreshSponsors={refreshSponsors} setDrawerOpen={setDrawerOpen} tiers={tiers} partners={partners}/>
            <AddSponsorModal isOpen={isAddModalOpen} onClose={() => setAddModalOpen(false)}
                             setSnackbarMessage={setSnackbarMessage} setSnackbarOpen={setSnackbarOpen}
                             refreshSponsors={refreshSponsors} eventId={eventId} tiers={tiers} partners={partners}/>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
            />
            <Snackbar
                open={snackbarDeleteOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarDeleteClose}
                message="Sponsor deleted successfully."
            />
        </Box>
    );
}